import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { IoCartOutline, IoMenu, IoClose } from "react-icons/io5";
import { useAuth } from "../context/AuthContext";
import { FaUserCircle } from "react-icons/fa";
import ConfirmationModal from "./ConfirmationModal";
import toast from "react-hot-toast";
import CartSidebar from "../components/CartSidebar";
import { useCart } from "../context/CartContext";
import { useExtra } from "../context/ExtraContext";
import {
  ChevronDown,
  LogOut,
  Package,
  Search,
  User,
  UserRound,
} from "lucide-react";
import { useCollections } from "../context/CollectionsContext";
import logo from "../assets/logo.png";
import AnnouncementMarquee from "../components/AnnouncementMarquee";
import DesktopCollections from "../components/DesktopCollections";
import MainCategoryNavigation, {
  usePrimaryNavItems,
} from "../components/MainCategoryNavigation";
import { useSearch } from "../context/SearchContext";

function useScrollDirection({ threshold = 8 } = {}) {
  const [dir, setDir] = useState("up"); // "up" | "down"
  const [atTop, setAtTop] = useState(true); // is near top?
  const lastY = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || 0;
      setAtTop(y < 4);
      const delta = y - lastY.current;
      if (Math.abs(delta) > threshold) {
        setDir(delta > 0 ? "down" : "up");
        lastY.current = y;
      }
    };
    onScroll(); // initialize
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return { dir, atTop };
}

function Header() {
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [dropdownOpenLogin, setDropdownOpenlogin] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { activeCollection, setActiveCollection, scrollToProducts } =
    useExtra();
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const { collections } = useCollections();
  const { categoryId } = useParams();
  const location = useLocation();

  const { setOpen } = useSearch();
  const { cartItems } = useCart();
  const cartCount = cartItems?.length || 0;
  const { items: primaryNavItems } = usePrimaryNavItems();

  const toggleDropdownLogin = () => setDropdownOpenlogin(!dropdownOpenLogin);
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  const handleLogoutConfirm = async () => {
    setLoggingOut(true);
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      toast.error("Logout failed");
      console.error("Logout failed:", err);
    } finally {
      setLoggingOut(false);
      setShowModal(false);
    }
  };

  // SAFE: route "/" when collectionId is null
  const handleCollectionSelect = (collectionId, subCategoryId = null) => {
    setTimeout(() => setMobileMenuOpen(false), 100);
    setActiveCollection(collectionId);
    setOpenDropdownId(null);
    scrollToProducts();
    if (collectionId == null) {
      navigate("/");
      return;
    }
    if (subCategoryId) {
      navigate(`/collections/${collectionId}/categories/${subCategoryId}`);
    } else {
      navigate(`/collections/${collectionId}`);
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownId(null);
        setDropdownOpenlogin(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close login dropdown on route change
  useEffect(() => {
    setDropdownOpenlogin(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      // Prevent scrolling on the body
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      // Restore scrolling
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [mobileMenuOpen]);

  // Scroll behavior
  const { dir, atTop } = useScrollDirection({ threshold: 6 });
  const scrolled = !atTop;
  const hidden = dir === "down" && scrolled;

  // Transparent header over full-bleed leather hero (collection listing only)
  const isCollectionRoute = (() => {
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts[0] === "collections" && parts[1]) return true;
    if (parts[0] === "category" && parts[1]) return true;
    return false;
  })();
  const overHero = isCollectionRoute && !scrolled;
  const iconTone = overHero
    ? "text-white hover:text-white/85"
    : "text-gray-700";

  // === NEW: fixed stack (Marquee + Header) with spacer ===
  const stackRef = useRef(null);
  const [stackH, setStackH] = useState(0);
  useEffect(() => {
    const el = stackRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const h = entry?.contentRect?.height || 0;
      setStackH(Math.round(h));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <>
      {/* Spacer to prevent layout jump; equals the height of [Marquee + Header] */}
      {/* Over hero: no spacer so the image bleeds under the transparent header */}
      <div style={{ height: overHero ? 0 : stackH }} />

      {/* Fixed stack that contains BOTH the marquee and the header */}
      <div
        ref={stackRef}
        className="fixed top-0 left-0 right-0 z-40"
        style={{
          transform: hidden ? "translateY(-100%)" : "translateY(0)",
          transition: "transform 300ms ease",
          willChange: "transform",
        }}
      >
        {/* --- Announcement Bar (top) --- */}
        {!scrolled && !overHero && <AnnouncementMarquee />}

        {/* --- Header (below marquee) --- */}
        <header
          className={[
            "flex items-center justify-between w-full px-4 md:px-10 h-14 transition-colors duration-300",
            overHero
              ? "bg-transparent border-b border-transparent"
              : scrolled
              ? "bg-white/70 supports-[backdrop-filter]:backdrop-blur border-b border-gray-200 shadow-sm"
              : "bg-white/95 border-b border-transparent",
          ].join(" ")}
        >
          {/* LEFT (mobile only: search, profile, cart) */}
          {/* RIGHT (mobile only: hamburger) */}
          <div className="flex lg:hidden">
            <button
              onClick={toggleMobileMenu}
              className={`rounded-lg transition-all duration-200 ${
                overHero
                  ? "text-white hover:text-white/85"
                  : "text-gray-700 hover:text-primary hover:bg-gray-50"
              }`}
              aria-label="Menu"
            >
              {mobileMenuOpen ? "" : <IoMenu size={22} />}
            </button>
          </div>

          {/* CENTER (logo: absolute center on mobile, normal on desktop) */}
          <Link
            to="/"
            className="absolute left-1/2 -translate-x-1/2 lg:static lg:translate-x-0 flex-shrink-0"
          >
            <img
              src={logo}
              alt="Logo"
              width={64}
              height={46}
              className={[
                "w-[64px] h-auto hover:scale-105 transition-transform duration-200",
                overHero ? "brightness-0 invert" : "",
              ].join(" ")}
              loading="eager"
              fetchPriority="high"
            />
          </Link>

          <div className="flex items-center  lg:hidden">
            <button
              onClick={() => setOpen(true)}
              className={`p-2 ${iconTone}`}
              aria-label="Search"
            >
              <Search className="w-5 h-5" />
            </button>

         

            <button
              onClick={() => setSidebarOpen(true)}
              className={`relative p-2 ${iconTone}`}
              aria-label="Cart"
            >
              <IoCartOutline className="text-[22px]" />
              {cartCount > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 flex items-center justify-center text-[10px] font-bold bg-[#C6A75E] text-white rounded-full px-0.5">
                  {cartCount > 9 ? "9+" : cartCount}
                </span>
              )}
            </button>
          </div>

          {/* Desktop Main Category Navigation */}
          <div
            className={[
              "hidden lg:flex items-center",
              overHero
                ? "[&_a]:text-white [&_button]:text-white [&_svg]:text-white"
                : "",
            ].join(" ")}
          >
            <MainCategoryNavigation />
          </div>


          {/* Desktop Right Nav */}
          <nav
            className={[
              "hidden lg:flex items-center gap-2 relative",
              overHero ? "text-white" : "text-black",
            ].join(" ")}
          >
            {/* Track Order - Visible to Everyone */}
            <Link
              to="/track-order"
              className={`relative flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 group ${
                overHero ? "hover:bg-white/10" : "hover:bg-gray-50"
              } ${
                location.pathname === "/track-order"
                  ? "after:absolute after:bottom-0 after:left-3 after:right-3 after:h-0.5 after:bg-[#C6A75E] after:rounded-full"
                  : ""
              }`}
              title="Track Order"
            >
              <svg
                className={`w-5 h-5 transition-colors duration-200 ${
                  overHero
                    ? "text-white group-hover:text-white/85"
                    : "text-gray-700 group-hover:text-primary"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 013.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              <span
                className={`text-sm font-medium transition-colors duration-200 ${
                  overHero
                    ? "text-white group-hover:text-white/85"
                    : "text-gray-700 group-hover:text-primary"
                }`}
              >
                Track
              </span>
            </Link>
            
            {!user ? (
              <>
                <div
                  className={`relative p-2 rounded-lg transition-all duration-200 group ${
                    overHero ? "hover:bg-white/10" : "hover:bg-gray-50"
                  }`}
                >
                  <Search
                    onClick={() => setOpen(true)}
                    className={`text-sm w-5 cursor-pointer transition-colors duration-200 ${
                      overHero
                        ? "text-white group-hover:text-white/85"
                        : "text-gray-700 group-hover:text-primary"
                    }`}
                  />
                </div>
                <Link
                  to="/login"
                  className={`relative p-2 rounded-lg transition-all duration-200 group ${
                    overHero ? "hover:bg-white/10" : "hover:bg-gray-50"
                  }`}
                >
                  <UserRound
                    className={`text-sm w-5 transition-colors duration-200 ${
                      overHero
                        ? "text-white group-hover:text-white/85"
                        : "text-gray-700 group-hover:text-primary"
                    }`}
                  />
                </Link>
              </>
            ) : (
              <div className="relative">
                <button
                  onClick={toggleDropdownLogin}
                  className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 group ${
                    overHero ? "hover:bg-white/10" : "hover:bg-gray-50"
                  }`}
                >
                  <FaUserCircle
                    className={`text-2xl transition-colors duration-200 ${
                      overHero
                        ? "text-white group-hover:text-white/85"
                        : "text-gray-600 group-hover:text-primary"
                    }`}
                  />
                  <span
                    className={`hidden lg:inline text-sm font-medium transition-colors duration-200 ${
                      overHero
                        ? "text-white group-hover:text-white/85"
                        : "text-gray-700 group-hover:text-primary"
                    }`}
                  >
                    {user.name}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 transition-colors duration-200 ${
                      overHero
                        ? "text-white/80 group-hover:text-white"
                        : "text-gray-500 group-hover:text-primary"
                    }`}
                  />
                </button>
                {dropdownOpenLogin && (
                  <div
                    ref={dropdownRef}
                    className="absolute right-0 mt-3 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-in slide-in-from-top-2 duration-200"
                  >
                    <div className="p-4 border-b border-gray-100 bg-gray-50">
                      <p className="font-semibold text-gray-900">{user.name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                    <div className="py-2">
                      <button
                        onClick={() => navigate("/profile")}
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-[#F4F1EC] transition-colors duration-200 group"
                      >
                        <div className="p-2 rounded-lg bg-gray-100 group-hover:bg-[#e8e3db] transition-colors duration-200">
                          <User className="h-4 w-4 text-gray-600 group-hover:text-primary" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium">My Profile</p>
                          <p className="text-xs text-gray-500">
                            Manage your account
                          </p>
                        </div>
                      </button>
                      <button
                        onClick={() => navigate("/my-orders")}
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-[#F4F1EC] transition-colors duration-200 group"
                      >
                        <div className="p-2 rounded-lg bg-gray-100 group-hover:bg-[#e8e3db] transition-colors duration-200">
                          <Package className="h-4 w-4 text-gray-600 group-hover:text-primary" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium">My Orders</p>
                          <p className="text-xs text-gray-500">
                            View order history
                          </p>
                        </div>
                      </button>
                      <button
                        onClick={() => navigate("/track-order")}
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-[#F4F1EC] transition-colors duration-200 group"
                      >
                        <div className="p-2 rounded-lg bg-gray-100 group-hover:bg-[#e8e3db] transition-colors duration-200">
                          <svg className="h-4 w-4 text-gray-600 group-hover:text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                          </svg>
                        </div>
                        <div className="text-left">
                          <p className="font-medium">Track Order</p>
                          <p className="text-xs text-gray-500">
                            Track your shipment
                          </p>
                        </div>
                      </button>
                    </div>
                    <div className="border-t border-gray-100"></div>
                    <div className="py-2">
                      <button
                        onClick={logout}
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200 group"
                      >
                        <div className="p-2 rounded-lg bg-red-100 group-hover:bg-red-200 transition-colors duration-200">
                          <LogOut className="h-4 w-4 text-red-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium">Sign Out</p>
                          <p className="text-xs text-red-500">
                            Logout from your account
                          </p>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(true)}
              className={`relative p-2 rounded-lg transition-all duration-200 group ${
                overHero ? "hover:bg-white/10" : "hover:bg-gray-50"
              }`}
            >
              <IoCartOutline
                className={`text-2xl transition-colors duration-200 ${
                  overHero
                    ? "text-white group-hover:text-white/85"
                    : "text-gray-700 group-hover:text-primary"
                }`}
              />
              {cartCount > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 flex items-center justify-center text-[10px] font-bold bg-[#C6A75E] text-white rounded-full px-0.5">
                  {cartCount > 9 ? "9+" : cartCount}
                </span>
              )}
            </button>
          </nav>

          {/* Mobile Slide Menu (unchanged) */}
          {/* Enhanced Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden fixed inset-0 z-[9999] bg-[#F4F1EC]">
              <div className="relative flex items-center justify-center px-3 py-3 bg-[#F4F1EC]">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex-shrink-0"
                >
                  <img
                    src={logo}
                    alt="ZAAR"
                    width={64}
                    height={46}
                    className="w-[64px] h-auto"
                  />
                </Link>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full text-[#2E2E2E] active:bg-black/5"
                  aria-label="Close menu"
                >
                  <IoClose size={24} />
                </button>
              </div>

              <div className="h-[calc(100vh-64px)] overflow-y-auto px-4 pb-12 bg-[#F4F1EC]">
                <nav className="flex flex-col px-1 pt-1">
                  {primaryNavItems.map((item) => (
                    <Link
                      key={item.label}
                      to={item.to}
                      className="block py-4 font-lora text-[17px] text-[#1C1C1C] leading-none"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </nav>
              </div>
            </div>
          )}

          <ConfirmationModal
            isOpen={showModal}
            title="Confirm Logout"
            message="Are you sure you want to log out?"
            onConfirm={handleLogoutConfirm}
            onCancel={() => setShowModal(false)}
            loading={loggingOut}
          />
        </header>
      </div>
      <CartSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
    </>
  );
}

export default Header;
